import { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button-tw";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/check-box";
import { Label } from "@/components/ui/label";
import { cn } from "@/utils/classnames";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  useCreateClientSettings,
  SettingKey,
  SettingValueType,
  SettingCategory,
} from "@/queries/client-settings";

interface BusinessInfo {
  businessName: string;
  businessEmail: string;
  businessAddress: string;
  businessAddressLine2: string;
  businessCity: string;
  businessState: string;
  businessZip: string;
  hideAddress: boolean;
}

interface DefaultInvoiceInfoSectionProps {
  userId: string;
  isAdmin: boolean;
  initialData: BusinessInfo | null;
  onUpdate: (data: BusinessInfo) => void;
}

export const DefaultInvoiceInfoSection = ({
  userId,
  isAdmin,
  initialData,
  onUpdate,
}: DefaultInvoiceInfoSectionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>({
    businessName: "",
    businessEmail: "",
    businessAddress: "",
    businessAddressLine2: "",
    businessCity: "",
    businessState: "",
    businessZip: "",
    hideAddress: false,
  });
  const [originalBusinessInfo, setOriginalBusinessInfo] =
    useState<BusinessInfo>({
      businessName: "",
      businessEmail: "",
      businessAddress: "",
      businessAddressLine2: "",
      businessCity: "",
      businessState: "",
      businessZip: "",
      hideAddress: false,
    });

  const createClientSettingsMutation = useCreateClientSettings({
    onSuccess: () => {
      toast.success("Invoice settings saved successfully!");
    },
    onError: (error) => {
      toast.error("Failed to save invoice settings. Please try again.");
      console.error("Error saving invoice settings:", error);
    },
  });

  useEffect(() => {
    if (initialData) {
      setBusinessInfo(initialData);
      setOriginalBusinessInfo(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    onUpdate(businessInfo);
  }, [businessInfo, onUpdate]);

  const updateBusinessInfo = (field: string, value: string | boolean) => {
    setBusinessInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    if (!userId) return;

    const settings: Array<{
      user_id: string;
      key: SettingKey;
      category: SettingCategory;
      value: any;
      value_type: SettingValueType;
      description: string;
      active: boolean;
    }> = [];

    if (businessInfo.businessName !== originalBusinessInfo.businessName) {
      settings.push({
        user_id: userId,
        key: SettingKey.BUSINESS_NAME,
        category: SettingCategory.BRANDING,
        value: { business_name: businessInfo.businessName },
        value_type: SettingValueType.STRING,
        description: "Business name for invoices",
        active: true,
      });
    }
    if (businessInfo.businessEmail !== originalBusinessInfo.businessEmail) {
      settings.push({
        user_id: userId,
        key: SettingKey.BUSINESS_EMAIL,
        category: SettingCategory.BRANDING,
        value: { business_email: businessInfo.businessEmail },
        value_type: SettingValueType.STRING,
        description: "Business email for invoices",
        active: true,
      });
    }
    if (businessInfo.businessAddress !== originalBusinessInfo.businessAddress) {
      settings.push({
        user_id: userId,
        key: SettingKey.BUSINESS_ADDRESS,
        category: SettingCategory.BRANDING,
        value: { business_address: businessInfo.businessAddress },
        value_type: SettingValueType.STRING,
        description: "Business address for invoices",
        active: true,
      });
    }
    if (businessInfo.businessCity !== originalBusinessInfo.businessCity) {
      settings.push({
        user_id: userId,
        key: SettingKey.BUSINESS_CITY,
        category: SettingCategory.BRANDING,
        value: { business_city: businessInfo.businessCity },
        value_type: SettingValueType.STRING,
        description: "Business city for invoices",
        active: true,
      });
    }
    if (businessInfo.businessState !== originalBusinessInfo.businessState) {
      settings.push({
        user_id: userId,
        key: SettingKey.BUSINESS_STATE,
        category: SettingCategory.BRANDING,
        value: { business_state: businessInfo.businessState },
        value_type: SettingValueType.STRING,
        description: "Business state for invoices",
        active: true,
      });
    }
    if (businessInfo.businessZip !== originalBusinessInfo.businessZip) {
      settings.push({
        user_id: userId,
        key: SettingKey.BUSINESS_ZIP,
        category: SettingCategory.BRANDING,
        value: { business_zip: businessInfo.businessZip },
        value_type: SettingValueType.STRING,
        description: "Business zip code for invoices",
        active: true,
      });
    }
    if (
      businessInfo.businessAddressLine2 !==
      originalBusinessInfo.businessAddressLine2
    ) {
      settings.push({
        user_id: userId,
        key: SettingKey.BUSINESS_ADDRESS_2,
        category: SettingCategory.BRANDING,
        value: { business_address_line2: businessInfo.businessAddressLine2 },
        value_type: SettingValueType.STRING,
        description: "Business address line 2 for invoices",
        active: true,
      });
    }
    if (businessInfo.hideAddress !== originalBusinessInfo.hideAddress) {
      settings.push({
        user_id: userId,
        key: SettingKey.HIDE_ADDRESS,
        category: SettingCategory.BRANDING,
        value: { hide_address: businessInfo.hideAddress },
        value_type: SettingValueType.BOOLEAN,
        description: "Hide address on invoices",
        active: true,
      });
    }

    if (settings.length === 0) return;

    createClientSettingsMutation.mutate(
      { settings },
      {
        onSuccess: () => {
          setOriginalBusinessInfo(businessInfo);
        },
      }
    );
  };

  const handleCancel = () => {
    setBusinessInfo(originalBusinessInfo);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="bg-white rounded-lg shadow">
        <CollapsibleTrigger className="w-full p-6 flex items-center justify-between hover:bg-gray-50 transition-colors">
          <h3 className="text-lg font-semibold text-gray-900">
            Default Invoice Information
          </h3>
          <ChevronDown
            className={cn(
              "h-5 w-5 text-gray-500 transition-transform",
              isOpen && "transform rotate-180"
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-6 pb-6 space-y-4 border-t border-gray-100">
            <div className="mt-4">
              <Label
                htmlFor="business-name"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Business Name
              </Label>
              <Input
                id="business-name"
                type="text"
                placeholder="Enter business name"
                value={businessInfo.businessName}
                onChange={(e) =>
                  updateBusinessInfo("businessName", e.target.value)
                }
                disabled={isAdmin}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label
                  htmlFor="business-address"
                  className="text-sm font-medium text-gray-700"
                >
                  Business Address
                </Label>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="hide-address"
                    checked={businessInfo.hideAddress}
                    onCheckedChange={(checked) =>
                      updateBusinessInfo("hideAddress", checked)
                    }
                    disabled={isAdmin}
                  />
                  <Label
                    htmlFor="hide-address"
                    className="text-sm text-gray-700 font-normal"
                  >
                    Do not display address on invoices
                  </Label>
                </div>
              </div>
              <Input
                id="business-address"
                type="text"
                placeholder="Street address"
                value={businessInfo.businessAddress}
                onChange={(e) =>
                  updateBusinessInfo("businessAddress", e.target.value)
                }
                disabled={isAdmin}
              />
            </div>

            <div>
              <Input
                id="business-address-line2"
                type="text"
                placeholder="Apartment, suite, etc. (optional)"
                value={businessInfo.businessAddressLine2}
                onChange={(e) =>
                  updateBusinessInfo("businessAddressLine2", e.target.value)
                }
                disabled={isAdmin}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Input
                  id="business-city"
                  type="text"
                  placeholder="City"
                  value={businessInfo.businessCity}
                  onChange={(e) =>
                    updateBusinessInfo("businessCity", e.target.value)
                  }
                  disabled={isAdmin}
                />
              </div>
              <div>
                <Input
                  id="business-state"
                  type="text"
                  placeholder="State"
                  value={businessInfo.businessState}
                  onChange={(e) =>
                    updateBusinessInfo("businessState", e.target.value)
                  }
                  disabled={isAdmin}
                />
              </div>
              <div>
                <Input
                  id="business-zip"
                  type="text"
                  placeholder="Zip code"
                  value={businessInfo.businessZip}
                  onChange={(e) =>
                    updateBusinessInfo("businessZip", e.target.value)
                  }
                  disabled={isAdmin}
                />
              </div>
            </div>

            <div>
              <Label
                htmlFor="business-email"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Email Address
              </Label>
              <Input
                id="business-email"
                type="email"
                placeholder="business@example.com"
                value={businessInfo.businessEmail}
                onChange={(e) =>
                  updateBusinessInfo("businessEmail", e.target.value)
                }
                disabled={isAdmin}
              />
            </div>

            {!isAdmin && (
              <div className="flex justify-end gap-3 pt-4 ">
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-600/80 text-white"
                  onClick={handleSave}
                  disabled={createClientSettingsMutation.isPending}
                >
                  {createClientSettingsMutation.isPending
                    ? "Saving..."
                    : "Save Changes"}
                </Button>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
